import TrainingRedirectionPopup from "@crescentParts/Components/Training/TrainingRedirectionPopup";
import TrainingRegistrationPopup from "@crescentParts/Components/Training/TrainingRegistrationPopup";
import calendarIcon from "@crescentParts/Icons/Training/Calendar";
import locationIcon from "@crescentParts/Icons/Training/Location";
import peuIcon from "@crescentParts/Icons/Training/Peu";
import restrictionIcon from "@crescentParts/Icons/Training/Restriction";
import tagIcon from "@crescentParts/Icons/Training/Tag";
import getTrainingProduct from "@crescentParts/Store/Data/TrainingProducts/Handler/TrainingProductHandler";
import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import loadRealTimeInventory from "@insite/client-framework/Store/CommonHandlers/LoadRealTimeInventory";
import { getCurrentPage, getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType, useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import LocalizedDateTime from "@insite/content-library/Components/LocalizedDateTime";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import LazyImage from "@insite/mobius/LazyImage";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import parse from "html-react-parser";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapDispatchToProps = {
    getTrainingProduct,
    loadRealTimeInventory
};

const mapStateToProps = (state: ApplicationState) => {
    const createAccountPageLink = getPageLinkByPageType(state, "CreateAccountPage");
    return {
        page: getCurrentPage(state),
        session: state.context.session,
        createAccountUrl: createAccountPageLink ? `${createAccountPageLink.url}` : undefined,
        signInUrl: getPageLinkByPageType(state, "SignInPage")?.url,
        location: getLocation(state),
        cartPageUrl: getPageLinkByPageType(state, "CartPage")?.url,
    }
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface NewsPageViewStyles {
    authorText?: TypographyProps;
    publishDateText?: TypographyProps;
    newsWrapper?: InjectableCss;
    contentWrapper?: InjectableCss;
    dateAndAuthorWrapper?: InjectableCss;
    crescentPartsTrainingContainer?: InjectableCss;
    trainingGridContainer?: InjectableCss;
    trainingImageContainer?: InjectableCss;
    trainingDetailsContainer?: InjectableCss;
    trainingDescriptionContainer?: InjectableCss;
    detailsWrapper?: InjectableCss;
    detailIcon?: IconPresentationProps;
    location?: TypographyPresentationProps;
    date?: TypographyPresentationProps;
    endDate?: TypographyPresentationProps;
    price?: TypographyPresentationProps;
    trainingType?: TypographyPresentationProps;
    registerNowButton?: ButtonPresentationProps;
    datesWrapper?: InjectableCss;
    trainingRedirectionModal?: ModalPresentationProps;
    qty?: TypographyPresentationProps;
    btnSpinner?: LoadingSpinnerProps;
}

export const newsPageViewStyles: NewsPageViewStyles = {
    newsWrapper: {},
    contentWrapper: {
        css: css`
            margin-top: 8px;
        `,
    },
    authorText: {
        css: css`
            font-weight: bold;
            margin-right: 8px;
        `,
    },
    publishDateText: {
        css: css`
            font-style: italic;
        `,
    },
    crescentPartsTrainingContainer: {
        css: css``,
    },
    trainingGridContainer: {
        css: css`
            display: grid;
            gap: 50px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            grid-template-columns: repeat(1, 1fr);
                        `,
                        null,
                        css`
                            grid-template-columns: 1fr 2fr;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    trainingImageContainer: {
        css: css`
            > [class^="LazyImageStyle"] {
                width: 100%;
                max-height: 200px;
                > img {
                    height: 100%;
                    object-fit: contain;
                    border-radius: 8px;
                }
            }
        `,
    },
    trainingDescriptionContainer: {
        css: css`
            margin-top: 20px;
        `,
    },
    detailsWrapper: {
        css: css`
            margin-bottom: 8px;
            display: flex;
            align-items: start;
        `,
    },
    detailIcon: {
        size: 16,
        css: css`
            margin-right: 14px;
            margin-top: 3px;
        `,
    },
    location: {
        variant: "p",
        color: "text.accent",
        css: css`
            margin-bottom: 0;
        `,
    },
    datesWrapper: {
        css: css`
            display: flex;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            flex-direction: column;
                        `,
                        css`
                            flex-direction: row;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    date: {
        variant: "p",
        color: "text.accent",
        css: css`
            margin-bottom: 0;
        `,
    },
    endDate: {
        variant: "p",
        color: "text.accent",
        css: css`
            margin-top: 0;
            margin-bottom: 0;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            margin-top: 8px;
                        `,
                    ],
                    "max",
                )}
        `,
    },
    price: {
        variant: "p",
        color: "text.accent",
        css: css`
            margin-bottom: 0;
        `,
    },
    trainingType: {
        variant: "headerTertiary",
        color: "#636363",
        css: css`
            text-transform: capitalize;
            margin-bottom: 5px;
        `,
    },
    registerNowButton: {
        css: css`
            margin-top: 5px;
        `,
    },
    trainingRedirectionModal: {
        sizeVariant: "small",
    },
    qty: {
        variant: "p",
        color: "text.accent",
        css: css`
            margin-bottom: 0;
            color: #79242F;
            font-weight: 700;
        `,
    },
    btnSpinner: {
        css: css`
            margin: auto;
            width: 20px;
            height: 16px;
        `,
    },
};

const styles = newsPageViewStyles;

const getTime = (trainingDate: string) => {
    const time = formatAMPM(new Date(trainingDate));
    return time;
};

const formatAMPM = (date: Date) => {
    let hours = date.getHours();
    let minutes: string | number = date.getMinutes();
    const ampm = hours >= 12 ? "pm" : "am";
    hours = hours % 12;
    hours = hours || 12; // the hour '0' should be '12'
    minutes = minutes < 10 ? `0${minutes}` : minutes;
    const time = `${hours}:${minutes} ${ampm}`;
    return time;
};

const NewsPageView = ({ page, session, createAccountUrl, signInUrl, location, cartPageUrl, getTrainingProduct, loadRealTimeInventory }: Props) => {
    const [registrationModalOpen, setRegistrationModalOpen] = React.useState<boolean>(false);
    const [selectedTraining, setSelectedTraining] = React.useState<string>("");
    const [selectedAddress, setSelectedAddress] = React.useState<string>("");
    const [selectedStartDate, setSelectedStartDate] = React.useState<string>("");
    const [selectedEndDate, setSelectedEndDate] = React.useState<string>("");
    const [trainingRedirectModalOpen, setTrainingRedirectModalOpen] = React.useState<boolean>(false);
    const [isInventoryLoading, setIsInventoryLoading] = React.useState<boolean>(false);
    const [trainingProductWithId, setTrainingProductWithId] = React.useState<any>(null);
    const [isCheckInventory, setIsCheckInventory] = React.useState<boolean>(false);
    const history = useHistory();
    if (!page) {
        return null;
    }

    React.useEffect(() => {
        if(session.isAuthenticated) {
            const trainingProduct = page.name;
            setIsInventoryLoading(true);
            getTrainingProduct({
                data: [trainingProduct],
                onSuccess: (result: any) => {    
                    const trainingWithId = [...result];
                    const productIds = trainingWithId.filter((product: any) => product.productId !== "" && !product.byPassInventory).map((product: any) => product.productId)
               
                    loadRealTimeInventory({
                        productIds,
                        onComplete: (data: any) => {
                            const realTimeInventory = data?.apiResult?.realTimeInventoryResults;
                            trainingWithId.forEach((training) => {
                                realTimeInventory.forEach((inventory: any) => {
                                    if(inventory.productId === training.productId) {
                                        training["qtyOnHand"] = inventory.qtyOnHand;
                                    }
                                });
                            });
                            setTrainingProductWithId(trainingWithId[0] || null);
                            setIsInventoryLoading(false);
                        },
                        onError(){
                            setTrainingProductWithId(trainingWithId[0] || null);
                            setIsInventoryLoading(false);
                        }
                    })
                }
           });
        }
    },[]);

    const url = page?.fields?.externalLink?.value || undefined;
    const {isInShell} = useShellContext();

    if (page?.fields?.blogType === "crescentParts") {
        const openModalHandler = (
            trainingTitle: string,
            trainingAddress: string,
            trainingStartDate: string,
            trainingEndDate: string,
            checkInventory?:boolean,
        ) => {
            if(!isInventoryLoading) {
                if(page.fields.registrationType === "regExternal" && url){
                    if (url.startsWith("http")) {
                        if (isInShell) {
                            return;
                        }
             
                        window.location.href = url;
                    } else {
                        history.push(url);
                    }
                } else if(session.isAuthenticated) {
                    setRegistrationModalOpen(true);
                    setSelectedTraining(trainingTitle);
                    setSelectedAddress(trainingAddress);
                    setSelectedStartDate(trainingStartDate);
                    setSelectedEndDate(trainingEndDate);
                    setIsCheckInventory(!!checkInventory)
                } else{
                    setTrainingRedirectModalOpen(true);
                } 
            }
        };

        const closeModalHandler = () => {
            setRegistrationModalOpen(false);
            setTrainingRedirectModalOpen(false);
            setSelectedTraining("");
            setSelectedAddress("");
            setSelectedStartDate("");
            setSelectedEndDate("");
            setIsCheckInventory(false);
        };
        const redirectHandler = (from: string) => {
            if(from === "createAccount") {
                if (!createAccountUrl) {
                    return;
                }
                history.push(createAccountUrl);
            } else {
                const returnUrl = location.pathname + location.search;
                    if (returnUrl === "/") {
                        history.push(`${signInUrl}`);
                    } else {
                        history.push(`${signInUrl}?returnUrl=${encodeURIComponent(returnUrl)}`);
                    }
            }
        }

        let seatAvailableMessage = "";
        if(trainingProductWithId?.byPassInventory){
            seatAvailableMessage = "Seat Availability - Open";
        }else if(trainingProductWithId?.qtyOnHand > 5) {
            seatAvailableMessage = "Seat Availability - Open";
        } else if(trainingProductWithId?.qtyOnHand <= 5 && trainingProductWithId?.qtyOnHand >= 1) {
            seatAvailableMessage = `Seat Availability - Only ${trainingProductWithId?.qtyOnHand} Seats Left!`;
        } else if(trainingProductWithId?.qtyOnHand === 0) {
            seatAvailableMessage = "Seat Availability - Class Full"
        }
        return (
            <StyledWrapper {...styles.crescentPartsTrainingContainer}>
                <StyledWrapper {...styles.trainingGridContainer}>
                    <StyledWrapper {...styles.trainingImageContainer}>
                        <LazyImage src={page?.fields?.trainingImage}></LazyImage>
                    </StyledWrapper>
                    <StyledWrapper {...styles.trainingDetailsContainer}>
                        <Typography {...styles.trainingType}>{page?.fields?.trainingType}</Typography>
                        {page?.fields?.location && (
                            <StyledWrapper {...styles.detailsWrapper}>
                                <Icon src={locationIcon} {...styles.detailIcon}></Icon>
                                <Typography {...styles.location}>{page?.fields?.location}</Typography>
                            </StyledWrapper>
                        )}

                        {page?.fields?.trainingDate && (
                            <StyledWrapper {...styles.detailsWrapper}>
                                <Icon src={calendarIcon} {...styles.detailIcon}></Icon>
                                <StyledWrapper {...styles.datesWrapper}>
                                    <Typography {...styles.date}>
                                        {getLocalizedDateTime({
                                            dateTime: new Date(page?.fields?.trainingDate),
                                        })}
                                        {" | "}
                                        {getTime(page?.fields?.trainingDate)}
                                        {page?.fields?.trainingEndDate && <>&nbsp;-&nbsp;</>}
                                    </Typography>
                                    {page?.fields?.trainingEndDate && (
                                        <Typography {...styles.endDate}>
                                            {getLocalizedDateTime({
                                                dateTime: new Date(page?.fields?.trainingEndDate),
                                            })}
                                            {" | "}
                                            {getTime(page?.fields?.trainingEndDate)}
                                        </Typography>
                                    )}
                                </StyledWrapper>
                            </StyledWrapper>
                        )}

                        {page?.fields?.price && (
                            <StyledWrapper {...styles.detailsWrapper}>
                                <Icon src={tagIcon} {...styles.detailIcon}></Icon>
                                <Typography {...styles.price}>{`$ ${page?.fields?.price}`}</Typography>
                            </StyledWrapper>
                        )}

                        {page?.fields?.peu && (
                            <StyledWrapper {...styles.detailsWrapper}>
                                <Icon src={peuIcon} {...styles.detailIcon}></Icon>
                                <Typography {...styles.location}>{page?.fields?.peu}</Typography>
                            </StyledWrapper>
                        )}

                        {page?.fields?.restriction && (
                            <StyledWrapper {...styles.detailsWrapper}>
                                <Icon src={restrictionIcon} {...styles.detailIcon}></Icon>
                                <Typography {...styles.location}>{page?.fields?.restriction}</Typography>
                            </StyledWrapper>
                        )}
                        {isInventoryLoading ? 
                            <StyledWrapper {...styles.detailsWrapper}>
                                Loading...
                            </StyledWrapper> :  
                            trainingProductWithId?.byPassInventory ? 
                                <StyledWrapper {...styles.detailsWrapper}>
                                    <Typography {...styles.qty}>
                                        {seatAvailableMessage}
                                    </Typography>
                                </StyledWrapper> : 
                            trainingProductWithId?.qtyOnHand !== undefined && (
                                <StyledWrapper {...styles.detailsWrapper}>
                                    <Typography {...styles.qty}>
                                        {seatAvailableMessage}
                                    </Typography>
                                </StyledWrapper>
                        )}
                        {page?.fields?.registrationType !== "regNotRequired" && 
                        <>
                        {
                            trainingProductWithId?.byPassInventory ? <Button
                            variant="secondary"
                            {...styles.registerNowButton}
                            disabled={trainingProductWithId?.qtyOnHand === 0}
                            onClick={() =>
                                openModalHandler(
                                    page?.name || "",
                                    page?.fields?.location || "",
                                    page?.fields?.trainingDate || "",
                                    page?.fields?.trainingEndDate || "",
                                    trainingProductWithId?.byPassInventory
                                )
                            }
                        >
                            {page?.fields?.buttonText}{isInventoryLoading && <LoadingSpinner {...styles.btnSpinner} />}
                        </Button> :
                            trainingProductWithId?.qtyOnHand === 0 ? (
                                <Button
                                    {...styles.registerNowButton}
                                    variant="secondary"
                                    onClick={() =>{
                                            openModalHandler(
                                                page?.name || "",
                                                page?.fields?.location || "",
                                                page?.fields?.trainingDate || "",
                                                page?.fields?.trainingEndDate || "",
                                                trainingProductWithId?.byPassInventory
                                            )
                                        }
                                    }
                                >
                                    {translate("Join training")}
                                </Button>
                            ) : (
                                <Button
                                    variant="secondary"
                                    {...styles.registerNowButton}
                                    disabled={trainingProductWithId?.qtyOnHand === 0}
                                    onClick={() =>
                                        openModalHandler(
                                            page?.name || "",
                                            page?.fields?.location || "",
                                            page?.fields?.trainingDate || "",
                                            page?.fields?.trainingEndDate || "",
                                            trainingProductWithId?.byPassInventory
                                        )
                                    }
                                >
                                    {page?.fields?.buttonText}{isInventoryLoading && <LoadingSpinner {...styles.btnSpinner} />}
                                </Button>
                            )
                        }
                        </>
                    }
                        <StyledWrapper {...styles.trainingDescriptionContainer}>
                            {page?.fields?.description}
                        </StyledWrapper>
                    </StyledWrapper>
                </StyledWrapper>
                <TrainingRegistrationPopup
                    selectedTraining={selectedTraining}
                    selectedAddress={selectedAddress}
                    selectedStartDate={selectedStartDate}
                    selectedEndDate={selectedEndDate}
                    isOpen={registrationModalOpen}
                    closeModalHandler={closeModalHandler}
                    showAddToCartConfirmationDialog={false}
                    hasEnoughInventory={undefined}
                    isAddToCartButtonDisabled={undefined}
                    trainingQty={trainingProductWithId?.qtyOnHand}
                    cartPageUrl={cartPageUrl}
                    isCheckInventory={isCheckInventory}
                    >
                </TrainingRegistrationPopup>
                <Modal
                    headline={translate("Create Account or Sign In")}
                    {...styles.trainingRedirectionModal}
                    isOpen={trainingRedirectModalOpen}
                    handleClose={() => setTrainingRedirectModalOpen(false)}>
                    <TrainingRedirectionPopup
                        redirectHandler={redirectHandler}
                    />
                </Modal>
            </StyledWrapper>
        );
    }

    return (
        <StyledWrapper {...styles.newsWrapper} key={page.id}>
            <StyledWrapper {...styles.dateAndAuthorWrapper}>
                {page.fields["author"] && (
                    <Typography {...styles.authorText} data-test-selector="newslist_author">
                        {page.fields["author"]}
                    </Typography>
                )}
                {page.fields["publishDate"] && (
                    <Typography {...styles.publishDateText} data-test-selector="newslist_publishdate">
                        <LocalizedDateTime
                            dateTime={new Date(page.fields["publishDate"])}
                            options={{ year: "numeric", month: "numeric", day: "numeric" }}
                        />
                    </Typography>
                )}
            </StyledWrapper>
            {(page.fields["newsSummary"] || page.fields["newsContent"]) && (
                <StyledWrapper {...styles.contentWrapper} data-test-selector="newslist_content">
                    {parse(page.fields["newsContent"], parserOptions)}
                </StyledWrapper>
            )}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(NewsPageView),
    definition: {
        group: "News Page",
        allowedContexts: ["NewsPage"],
    },
};

export default widgetModule;
